<script setup>
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import axios from 'axios';
import { DEV_MODE } from '@/config/dev-mode';
import { usePaymentsStore } from '@/store/modules/payments';
import { createOrder } from '@/api/PaymentApi';
import { useSnackbar } from '@/composables/useSnackbar';
import { useDialogManager } from '@/composables/useDialogManager';
import { useRnBridge } from '@/composables/useRnBridge';
import { validateName, validateMobile } from '@/composables/validationCheck';
import CustomInput from '@/components/auth/CustomInput.vue';
import ConfirmComponent from '@/components/common/Confirm.vue';
import Snackbar from '@/components/common/Snackbar.vue';

const store = useStore();
const paymentsStore = usePaymentsStore();
const route = useRoute();
const router = useRouter();
const { snackbarRef, triggerSnackbar } = useSnackbar();
const {
  dialogTitle,
  dialogTitle2,
  dialogText,
  dialogNextUrl,
  dialogWarning,
  confirmRef,
  showConfirm,
} = useDialogManager();
const { sendMessageToApp } = useRnBridge();
const props = defineProps({
  ticketData: {
    type: Object,
  },
  optionItems: {
    type: Object,
  },
  selectedOption: {
    type: Object,
  },
  totalPrice: {
    type: Number,
    default: 0,
    required: false,
  },
  totalBasePrice: {
    type: Number,
    default: 0,
    required: false,
  },
  accessToken: {
    type: String,
  },
});

const ticketData = computed(() => props.ticketData);
const isDialogVisible = ref(false);
const emit = defineEmits(['close']);

// 선물하기 단계 (1: 입력, 2: 확인, 3: 완료)
const step = ref(1);

// 선물하기 입력 필드
const giftSenderName = ref('');
const giftReceiverName = ref('');
const giftReceiverPhone = ref('');
const giftMessage = ref('');

// Validation 상태
const receiverNameValidation = reactive({
  isValid: false,
  validationMessage: '',
});
const receiverPhoneValidation = reactive({
  isValid: false,
  validationMessage: '',
});
const senderNameValidation = reactive({
  isValid: false,
  validationMessage: '',
});

// 이름 입력 핸들러
const inputNameHandler = (value, validation) => {
  if (value === '') {
    validation.isValid = false;
    validation.validationMessage = '';
  } else if (!validateName(value)) {
    validation.isValid = false;
    validation.validationMessage = '이름은 한글, 영문만 사용 가능합니다.';
  } else {
    validation.isValid = true;
    validation.validationMessage = '';
  }
};

// 휴대폰 번호 입력 핸들러
const inputMobileHandler = (value, validation) => {
  if (value === '') {
    validation.isValid = false;
    validation.validationMessage = '';
  } else if (!validateMobile(value)) {
    validation.isValid = false;
    validation.validationMessage = '올바르지 않은 전화번호입니다.';
  } else {
    validation.isValid = true;
    validation.validationMessage = '';
  }
};

// 통합 입력 핸들러
const inputHandler = ({ value, dataType }) => {
  value = value.trim();
  switch (dataType) {
    case 'giftReceiverName':
      inputNameHandler(value, receiverNameValidation);
      break;
    case 'giftReceiverPhone':
      value = value.replace(/\D/g, '');
      inputMobileHandler(value, receiverPhoneValidation);
      break;
    case 'giftSenderName':
      inputNameHandler(value, senderNameValidation);
      break;
    default:
      break;
  }
  return value;
};

// 버튼 비활성화 여부 (모든 필드가 유효해야 활성화)
const disabled = computed(
  () =>
    !receiverNameValidation.isValid ||
    !receiverPhoneValidation.isValid ||
    !senderNameValidation.isValid,
);

// 테스트 입력
const fillTestData = () => {
  giftReceiverName.value = '개발자';
  giftReceiverPhone.value = '01062424192';
  giftSenderName.value = '단감';
  inputHandler({ value: '개발자', dataType: 'giftReceiverName' });
  inputHandler({ value: '01062424192', dataType: 'giftReceiverPhone' });
  inputHandler({ value: '단감', dataType: 'giftSenderName' });
};

// 휴대폰 번호 포맷팅 (010-1234-5678)
const formattedReceiverPhone = computed(() => {
  const phone = giftReceiverPhone.value.replace(/\D/g, '');
  if (phone.length === 11) {
    return phone.replace(/(\d{3})(\d{4})(\d{4})/, '$1-$2-$3');
  } else if (phone.length === 10) {
    return phone.replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3');
  }
  return phone;
});

// 다음 단계로 이동
const goToNextStep = () => {
  step.value = 2;
};

// 재입력 (1단계로 돌아가기)
const goToPrevStep = () => {
  step.value = 1;
};

/** 잔여 포인트 체크 */
const isShortage = computed(
  () => Number(totalPoint.value.replaceAll(',', '')) > remainPoints.value,
);
const remainPoints = computed(() => paymentsStore.points || 0);
const shortageAmount = computed(() =>
  isShortage.value ? Number(totalPoint.value.replaceAll(',', '')) - remainPoints.value : 0,
);

const userId = ref(null);
userId.value = store.getters['user/id'];

const close = () => {
  isDialogVisible.value = false;
  emit('close');

  // 다이얼로그 닫힘 애니메이션 후 초기화 (300ms)
  setTimeout(() => {
    step.value = 1;

    // 입력값 초기화
    giftReceiverName.value = '';
    giftReceiverPhone.value = '';
    giftSenderName.value = '';
    giftMessage.value = '';

    // Validation 상태 초기화
    receiverNameValidation.isValid = false;
    receiverNameValidation.validationMessage = '';
    receiverPhoneValidation.isValid = false;
    receiverPhoneValidation.validationMessage = '';
    senderNameValidation.isValid = false;
    senderNameValidation.validationMessage = '';

    // sessionStorage 정리
    sessionStorage.removeItem('giftingData');
  }, 300);
};

const goToGiftBox = () => {
  close();
  router.push({ path: '/purchase/gift-box', query: { tab: 'outbox' } });
};

const totalBasePoint = computed(() => {
  const value = props.totalBasePrice || ticketData.value.product.basePointPrice;
  return value.toLocaleString();
});

const totalPoint = computed(() => {
  const value = props.totalPrice || ticketData.value.product.salePointPrice;
  return value.toLocaleString();
});

const discountedPoint = computed(() => {
  const original = props.totalBasePrice || ticketData.value.product.basePointPrice;
  const current = props.totalPrice || ticketData.value.product.salePointPrice;
  const discount = original - current;
  return discount.toLocaleString();
});
const name = computed(() => ticketData.value?.product?.name);
const category = computed(() => ticketData.value?.boardCategory?.categoryCtn);

const confirmLogin = () => {
  if (window.ReactNativeWebView) {
    sendMessageToApp({ action: 'login' });
  } else {
    router.push({ path: '/auth/login', query: { to: `${route.path}` } });
  }
};

const saveGiftingData = () => {
  const data = {
    giftSenderName: giftSenderName.value,
    giftReceiverName: giftReceiverName.value,
    giftReceiverPhone: giftReceiverPhone.value,
    giftMessage: giftMessage.value,
    step: step.value,
  };
  sessionStorage.setItem('giftingData', JSON.stringify(data));
};

const restoreGiftingData = () => {
  const saved = sessionStorage.getItem('giftingData');
  if (saved) {
    const data = JSON.parse(saved);
    giftSenderName.value = data.giftSenderName || '';
    giftReceiverName.value = data.giftReceiverName || '';
    giftReceiverPhone.value = data.giftReceiverPhone || '';
    giftMessage.value = data.giftMessage || '';
    step.value = data.step || 1;

    // Validation 상태 복원
    if (giftSenderName.value) inputNameHandler(giftSenderName.value, senderNameValidation);
    if (giftReceiverName.value) inputNameHandler(giftReceiverName.value, receiverNameValidation);
    if (giftReceiverPhone.value)
      inputMobileHandler(giftReceiverPhone.value, receiverPhoneValidation);

    sessionStorage.removeItem('giftingData');
    return true;
  }
  return false;
};

const confirmCharge = () => {
  saveGiftingData();

  let nextPath = route.fullPath;
  if (props.selectedOption?.optionId) {
    const url = new URL(nextPath, window.location.origin);
    url.searchParams.set('optionId', props.selectedOption.optionId);
    nextPath = url.pathname + url.search;
  }
  router.push({
    path: '/purchase/points',
    query: {
      next: nextPath,
      shortageAmount: shortageAmount.value,
    },
  });
};

const handlePurchase = () => {
  if (!userId.value && !props.accessToken) {
    showConfirm({
      title: '로그인 후 이용 가능합니다.',
      title2: '로그인 페이지로 이동하시겠습니까?',
      text: '확인',
      onConfirm: () => confirmLogin(),
    });
  } else if (Number(totalPoint.value.replaceAll(',', '')) > Number(paymentsStore.points)) {
    showConfirm({
      title: '보유 낭만 포인트가 부족합니다.',
      title2: '지금 바로 충전하시겠습니까?',
      text: '충전하기',
      onConfirm: () => confirmCharge(),
      warning: `${shortageAmount.value.toLocaleString()} P 부족`,
    });
  } else {
    showConfirm({
      title: '선택한 상품으로 포인트 결제를 진행합니다.',
      text: '확인',
      onConfirm: () => orderPlanTicket(),
    });
  }
};

const orderPlanTicket = async () => {
  const params = {
    amount: 0,
    pointAmount: props.totalPrice || ticketData.value.product.salePointPrice,
    originalAmount: 0,
    originalPointAmount: props.totalBasePrice || ticketData.value.product.basePointPrice,
    productId: ticketData.value.product.productId,
    selectedOptions: props.selectedOption ? [props.selectedOption] : [],
    orderName: `${ticketData.value.product.name}`,
    isGift: true,
    giftSenderName: giftSenderName.value,
    giftReceiverName: giftReceiverName.value,
    giftReceiverPhone: giftReceiverPhone.value,
    giftMessage: giftMessage.value,
  };

  try {
    await createOrder(params);
    paymentsStore.fetchTicketCount();
    paymentsStore.getOwnPoint();

    step.value = 3; // 완료 화면으로 이동
  } catch (error) {
    DEV_MODE && console.error('분석권구매실패', error);

    const errorMessage =
      error?.response?.data?.detail || error?.message || '구매 중 오류가 발생했습니다.';
    triggerSnackbar(errorMessage);
  }
};

const open = () => {
  isDialogVisible.value = true;
};

// 결제 내역 페이지로 이동
const goToPaymentHistory = () => {
  close();
  router.push('/mypage/history');
};

onMounted(() => {
  paymentsStore.getOwnPoint();
});

// 충전 후 돌아왔을 때 입력 정보 복원 (ticketData 준비된 후)
watch(
  () => props.ticketData,
  (newData) => {
    if (newData && sessionStorage.getItem('giftingData')) {
      restoreGiftingData();
      isDialogVisible.value = true;
    }
  },
  { immediate: true },
);

defineExpose({
  open,
  close,
});

watch(
  () => props.accessToken,
  (newValue) => {
    if (newValue) {
      axios.defaults.headers.common['Authorization'] = newValue;
      paymentsStore.getOwnPoint();
    }
  },
);
</script>

<template>
  <VDialog
    v-model="isDialogVisible"
    class="v-dialog-sm dialog-purchase"
    max-width="400px"
    persistent
    no-click-animation
  >
    <VCard class="br16">
      <div class="pop-inner">
        <div class="pop-tit-box">
          <div v-if="step === 1 || step === 2" class="">선물하기</div>
          <div class="close-button" @click="close">Close</div>
        </div>

        <div class="gifting-content" :class="{ 'step-2': step === 2 }">
          <!-- Step 1: 입력 단계 -->
          <template v-if="step === 1">
            <div class="gifting-content__body">
              <div class="gifting-title">
                <div class="gifting-title__category-wrapper">
                  <span class="bg-category">{{ category }}</span>
                </div>
                <div class="gifting-item">
                  <div class="gifting-item__name">{{ name }}</div>
                  <div class="gifting-item__price">결제 포인트 {{ totalPoint }} P</div>
                </div>
              </div>

              <div class="gifting-input__wrapper">
                <div class="gifting-input__label">
                  선물 받으실 분의 정보를 정확하게 입력해 주세요.
                  <button v-if="DEV_MODE" class="test-fill-btn" @click="fillTestData">
                    테스트 입력
                  </button>
                </div>
                <CustomInput
                  v-model="giftReceiverName"
                  data-type="giftReceiverName"
                  placeholder="받는 분의 이름을 입력해주세요"
                  :clearable="true"
                  :input-handler="inputHandler"
                  :validation-message="receiverNameValidation.validationMessage"
                />
                <CustomInput
                  v-model="giftReceiverPhone"
                  :max-length="11"
                  data-type="giftReceiverPhone"
                  type="text"
                  placeholder="받는 분의 연락처를 '-' 없이 입력해주세요"
                  :clearable="true"
                  :input-handler="inputHandler"
                  :validation-message="receiverPhoneValidation.validationMessage"
                />
                <div class="divider"></div>
                <div class="gifting-input__label">
                  선물 보내시는 분의 정보를 정확하게 입력해 주세요.
                </div>
                <CustomInput
                  v-model="giftSenderName"
                  data-type="giftSenderName"
                  placeholder="보내는 사람 이름을 입력해주세요"
                  :clearable="true"
                  :input-handler="inputHandler"
                  :validation-message="senderNameValidation.validationMessage"
                />
                <div class="gift-message__wrapper">
                  <textarea
                    v-model="giftMessage"
                    class="gift-message__textarea"
                    placeholder="전하고 싶은 메시지를 입력해주세요"
                    maxlength="50"
                    rows="2"
                  ></textarea>
                  <span class="gift-message__count">{{ giftMessage.length }}/50</span>
                </div>
                <div class="gifting-notice__box">
                  <div>＊ 본인에게는 선물할 수 없습니다.</div>
                  <div>＊ 입력하신 번호가 맞는지 한번 더 확인해주세요.</div>
                </div>
              </div>
            </div>

            <div class="btn-com">
              <button class="btn-nor cancel" @click="close">취소</button>
              <button
                class="btn-nor submit"
                :class="{ disabled }"
                :disabled="disabled"
                @click="goToNextStep"
              >
                다음
              </button>
            </div>
          </template>

          <!-- Step 2: 확인 단계 -->
          <template v-else-if="step === 2">
            <div class="gifting-content__body">
              <div class="gifting-title">
                <div class="gifting-title__category-wrapper">
                  <span class="bg-category">{{ category }}</span>
                </div>
                <div class="gifting-item">
                  <div class="gifting-item__name">{{ name }}</div>
                </div>
              </div>

              <div class="gifting-confirm__wrapper">
                <div class="gifting-confirm__row">
                  <span class="gifting-confirm__key">받는 분</span>
                  <span class="gifting-confirm__value">{{ giftReceiverName }}</span>
                </div>
                <div class="gifting-confirm__row">
                  <span class="gifting-confirm__key">휴대폰 번호</span>
                  <span class="gifting-confirm__value">{{ formattedReceiverPhone }}</span>
                </div>
                <div class="gifting-confirm__row">
                  <span class="gifting-confirm__key">보내시는 분</span>
                  <span class="gifting-confirm__value">{{ giftSenderName }}</span>
                </div>
                <div v-if="giftMessage" class="gifting-confirm__message">
                  <span class="gifting-confirm__value">{{ giftMessage }}</span>
                </div>
              </div>

              <div class="purchase-content-box">
                <div class="title-5">결제 내역</div>
                <div class="purchase-content-box-item">
                  <div class="purchase-content-box-item-title">상품가(옵션포함)</div>
                  <div class="purchase-content-box-item-price">{{ totalBasePoint }} P</div>
                </div>
                <div class="purchase-content-box-item">
                  <div class="purchase-content-box-item-title">상품할인</div>
                  <div class="purchase-content-box-item-price">- {{ discountedPoint }} P</div>
                </div>
              </div>

              <div class="purchase-content-box__wrapper">
                <div class="purchase-content-box-total">
                  <div class="purchase-content-box-total-title">총 결제할 포인트</div>
                  <div class="purchase-content-box-total-price">{{ totalPoint }} P</div>
                </div>
                <div>
                  <div class="purchase-content-box-total">
                    <div class="purchase-content-box-total-title">보유 포인트</div>
                    <div class="purchase-content-box-remain-point">
                      {{ remainPoints?.toLocaleString() }} P
                    </div>
                  </div>
                  <div class="purchase-content-box-total">
                    <div class="purchase-content-box-total-title">부족한 포인트</div>
                    <div class="purchase-content-box-remain-point">
                      {{ shortageAmount?.toLocaleString() }} P
                    </div>
                  </div>
                </div>
              </div>

              <div class="gifting-notice__box">
                <div>＊ 입력하신 정보는 선물 발송 후 수정할 수 없어요.</div>
              </div>
            </div>

            <div class="btn-com">
              <button class="btn-nor cancel" @click="goToPrevStep">재입력</button>
              <button class="btn-nor submit" @click="handlePurchase">선물하기</button>
            </div>
          </template>

          <!-- Step 3: 완료 단계 -->
          <template v-if="step === 3">
            <div class="gifting-step3">
              <div class="gifting-content__body gifting-complete">
                <img
                  class="gifting-complete__icon"
                  src="@/assets/images_manse/icon_check_circle.png"
                  alt="완료"
                />
                <div class="gifting-complete__message">
                  {{ giftReceiverName }} ({{ formattedReceiverPhone }}) 님에게<br />선물이
                  발송되었습니다.
                </div>
              </div>

              <div class="gifting-step3__bottom">
                <div class="btn-com">
                  <button class="btn-nor submit" @click="goToGiftBox">보낸 선물함 보기</button>
                </div>

                <div class="gifting-complete__footer-notice">
                  결제 완료된 내역은 이용중인 서비스에서 확인할 수 있습니다.
                  <span class="link" @click="goToPaymentHistory">이용중인 서비스 바로가기</span>
                </div>
              </div>
            </div>
          </template>
        </div>
      </div>
    </VCard>
  </VDialog>
  <ConfirmComponent
    ref="confirmRef"
    :title="dialogTitle"
    :title2="dialogTitle2"
    :text="dialogText"
    :nextUrl="dialogNextUrl"
    :warning="dialogWarning"
  />
  <Snackbar ref="snackbarRef" />
</template>

<style lang="scss">
.gifting-input__wrapper {
  .validation-message {
    font-size: 11px;
  }

  // iOS 줌인 방지 (16px 이상 필요)
  .custom-input-box {
    font-size: 16px;
  }
}
</style>

<style scoped lang="scss">
.gifting-content {
  padding: 10px 32px 32px;
  min-height: 300px;
  display: flex;
  flex-direction: column;
  gap: 32px;
  font-family: Pretendard;

  &.step-2 {
    gap: 16px;

    .gifting-content__body {
      gap: 16px;
    }
  }

  .gifting-content__body {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    gap: 6px;

    .gifting-title {
      display: flex;
      flex-direction: column;
      gap: 4px;
      font-style: normal;
      font-weight: 700;

      .gifting-title__category-wrapper {
        .bg-category {
          display: inline-flex;
          color: var(--gray-100-white, #fff);
          font-size: 12px;
          line-height: 16px; /* 133.333% */
          border-radius: 4px;
          background: var(--Gray-600, #757575);
          padding: 2px 5px;
          align-items: center;
          gap: 10px;
        }
      }

      .gifting-item {
        .gifting-item__name {
          color: var(--Gray-800, #333);
          font-size: 16px;
          line-height: 22px;
        }
        .gifting-item__price {
          color: var(--orange-300-main, #eb4c10);
          font-size: 12px;
          line-height: 22px;
        }
      }
    }

    .gifting-input__wrapper {
      display: flex;
      flex-direction: column;
      gap: 10px;

      .gifting-input__label {
        color: var(--Gray-800, #333);
        font-size: 13px;
        font-weight: 400;
        line-height: 20px;

        .test-fill-btn {
          margin-left: 8px;
          padding: 2px 8px;
          font-size: 11px;
          color: #fff;
          background: #666;
          border: none;
          border-radius: 4px;
          cursor: pointer;
        }
      }
      .divider {
        margin: 5px 0;
        width: 100%;
        height: 1px;
        background: #ebebeb;
      }

      .gift-message__wrapper {
        position: relative;

        .gift-message__textarea {
          width: 100%;
          padding: 12px 14px;
          padding-bottom: 28px;
          border-radius: 6px;
          border: 1px solid var(--Gray-300, #ddd);
          background: var(--gray-100-white, #fff);
          font-family: Pretendard;
          font-size: 16px; // iOS 줌인 방지
          line-height: 20px;
          color: var(--Gray-800, #333);
          resize: none;
          outline: none;
          box-sizing: border-box;

          &::placeholder {
            color: var(--Gray-400, #bbb);
          }

          &:focus {
            border-color: var(--orange-300-main, #eb4c10);
          }
        }

        .gift-message__count {
          position: absolute;
          right: 14px;
          bottom: 10px;
          color: var(--Gray-500, #999);
          font-size: 12px;
          font-weight: 400;
        }
      }
    }
    .gifting-notice__box {
      display: flex;
      flex-direction: column;
      gap: 5px;
      color: #e3472f;
      font-family: Pretendard;
      font-size: 11px;
      font-style: normal;
      font-weight: 400;
      line-height: 130%;
    }

    .gifting-confirm__wrapper {
      display: flex;
      flex-direction: column;
      gap: 8px;
      padding: 16px;
      border-radius: 16px;
      border: 1px solid #d9d9d9;
      color: var(--Gray-800, #333);
      font-size: 12px;

      .divider {
        width: 100%;
        height: 1px;
        background: #ddd;
      }

      .gifting-confirm__message {
        padding: 10px 12px;
        display: flex;
        flex-direction: column;
        border-radius: 12px;
        background-color: #f8f8f8;
        font-weight: 400;
        line-height: 20px;
      }

      .gifting-confirm__row {
        padding: 0px 12px;
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        line-height: 18px;

        .gifting-confirm__key {
          font-weight: 500;
          min-width: 50px;
        }

        .gifting-confirm__value {
          font-weight: 600;
          text-align: right;
          word-break: break-all;
        }
      }
    }

    .gifting-payment__wrapper {
      padding: 16px 0;

      .gifting-payment__row {
        display: flex;
        justify-content: space-between;
        align-items: center;

        .gifting-payment__label {
          color: var(--Gray-800, #333);
          font-size: 14px;
          font-weight: 700;
          line-height: 18px;
        }

        .gifting-payment__value {
          color: var(--orange-300-main, #eb4c10);
          font-size: 22px;
          font-weight: 700;
          line-height: 26px;
        }
      }
    }
  }

  .purchase-content-box-total {
    display: flex;
    justify-content: space-between;
    align-items: center;
    .purchase-content-box-total-title {
      color: var(--Gray-800, #333);
      font-family: Pretendard;
      font-size: 14px;
      font-style: normal;
      font-weight: 700;
      line-height: 18px; /* 128.571% */
    }
    .purchase-content-box-total-price {
      color: var(--orange-300-main, #eb4c10);
      font-family: Pretendard;
      font-size: 22px;
      font-style: normal;
      font-weight: 700;
      line-height: 26px;
    }
    .purchase-content-box-remain-point {
      color: var(--orange-300-main, #eb4c10);
      font-family: Pretendard;
      font-size: 18px;
      font-style: normal;
      font-weight: 500;
      line-height: 26px;
    }
  }
  .purchase-content-box__wrapper {
    padding: 16px 24px;
    display: flex;
    flex-direction: column;
    gap: 10px;
    border-radius: 12px;
    background: var(--Orange-100, #fff8f5);
  }
  .purchase-content-box {
    padding: 0 24px;
    display: flex;
    flex-direction: column;
    .title-5 {
      color: var(--Gray-800, #333);
      text-align: left;
      font-size: 14px;
      font-style: normal;
      font-weight: 700;
      line-height: 18px; /* 128.571% */
      margin-bottom: 12px;
    }
    .purchase-content-box-item {
      display: flex;
      padding: 4px 12px;
      justify-content: space-between;
      align-items: center;
      align-self: stretch;
      font-size: 14px;
    }
  }

  .btn-com {
    width: 100%;
    display: flex;
    gap: 8px;

    .cancel {
      font-size: 16px;
      font-weight: 700;
      line-height: 20px;
    }

    .submit {
      font-size: 16px;
      font-weight: 700;
      line-height: 20px;

      &.disabled {
        background: #949494;
      }
    }
  }
}

.gifting-step3 {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 100%;
  gap: 40px;

  .gifting-complete {
    gap: 40px;
  }

  &__bottom {
    display: flex;
    flex-direction: column;
    gap: 40px;

    .btn-com {
      width: 100%;
    }
  }
}

.gifting-complete {
  align-items: center;
  text-align: center;
  gap: 40px;

  &__icon {
    width: 80px;
    height: 80px;
  }

  &__message {
    color: var(--gray-900-black, #2c2c2e);
    text-align: center;
    font-family: Pretendard;
    font-size: 20px;
    font-style: normal;
    font-weight: 700;
    line-height: 28px;
  }

  &__footer-notice {
    color: var(--Gray-700, #575757);
    font-family: Pretendard;
    font-size: 13px;
    font-style: normal;
    font-weight: 400;
    line-height: 18px;
    word-break: keep-all;
    text-align: center;

    .link {
      text-decoration: underline;
      cursor: pointer;
    }
  }
}

@media (max-width: 768px) {
  .gifting-content {
    padding: 0px 16px 16px;
    gap: 16px;

    .gifting-content__body {
      .gifting-confirm__wrapper {
        padding: 12px;
      }
      .purchase-content-box__wrapper {
        padding: 12px 16px;
      }
      .purchase-content-box {
        padding: 0 16px;
      }
    }
  }

  .gifting-step3 {
    gap: 32px;
    padding-bottom: 16px;

    .gifting-complete {
      gap: 32px;
    }

    &__bottom {
      gap: 32px;
    }
  }
}
</style>
